/**
 * galaxyPipeline — spiral galaxy with millions of stars.
 *
 * Stars distributed in spiral arms via logarithmic spirals. Each arm
 * is offset by an angle and the stars are placed with gaussian
 * dispersion perpendicular to the arm. The galaxy rotates, dust
 * lane obscures parts of the disk, and the bright nucleus glows.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface GalaxyPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Box-Muller transform — generate a gaussian random variable.
float gauss(vec2 seed) {
  float u1 = hash21(seed);
  float u2 = hash21(seed + 1.7);
  if (u1 < 1e-6) u1 = 1e-6;
  return sqrt(-2.0 * log(u1)) * cos(6.2831853 * u2);
}

// Compute the position of a single star in the galaxy (2D for simplicity).
// Arm index a, position along arm p.
vec2 starPos(float a, float p) {
  // Logarithmic spiral: r = r0 * exp(b * theta).
  // p is the position along the arm (0..1).
  float r = 0.05 + p * 1.0;
  float theta = log(r / 0.05) * 2.5 + a * 6.2831853 / 4.0 + uTime * 0.15 * (0.5 + p);
  // Add gaussian dispersion perpendicular to arm.
  float perp = gauss(vec2(a * 31.0, p * 17.0)) * (0.05 + p * 0.10);
  // Rotate perpendicular offset.
  vec2 dir = vec2(cos(theta), sin(theta));
  vec2 perpDir = vec2(-dir.y, dir.x);
  vec2 basePos = dir * r;
  vec2 pos = basePos + perpDir * perp;
  // Slight z-compression to suggest an oblique disk.
  pos.x *= 1.4;
  return pos;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= 1.4;

  // Background: deep space.
  vec3 col = vec3(0.005, 0.005, 0.015);

  // Dim background stars (separate from galaxy stars).
  vec2 starUv = floor(gl_FragCoord.xy / 2.0);
  float starSeed = hash21(starUv);
  float starMask = step(0.999, starSeed);
  col += vec3(0.85, 0.9, 1.0) * starMask * 0.4;

  // Galaxy stars: 4 spiral arms, ~300 stars per arm = 1200 total.
  const int N_PER_ARM = 60;
  const int N_ARMS = 4;
  for (int a = 0; a < N_ARMS; a++) {
    for (int i = 0; i < N_PER_ARM; i++) {
      float fa = float(a);
      float fi = float(i);
      float pParam = fi / float(N_PER_ARM);
      vec2 sp = starPos(fa, pParam);
      float d = length(p - sp);
      // Star brightness varies with arm position (brighter near core).
      float starBright = 0.6 + 0.4 * (1.0 - pParam);
      // Audio: brighter with high.
      starBright *= 1.0 + high * 0.5;
      // Color: warm yellow at the center, bluer at the edges.
      vec3 starColor = mix(
        vec3(1.0, 0.7, 0.4),
        vec3(0.6, 0.7, 1.0),
        pParam
      );
      // Glow.
      float radius = 0.005 + 0.003 * (1.0 - pParam);
      float glow = exp(-d * d / (radius * radius)) * starBright;
      col += starColor * glow * 1.5;
      // Outer halo.
      col += starColor * exp(-d * d / (radius * radius * 30.0)) * 0.05;
    }
  }

  // Dust lane: a dark band across the middle (obscures part of the disk).
  float dust = exp(-p.y * p.y * 15.0) * smoothstep(-0.3, 0.3, p.x);
  col *= 1.0 - dust * 0.6;

  // Bright nucleus.
  float nucleus = exp(-length(p) * 5.0);
  col += vec3(1.0, 0.85, 0.55) * nucleus * (1.5 + bass * 1.0);

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeGalaxyBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeGalaxyRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createGalaxyPipeline(gl: WebGL2RenderingContext): GalaxyPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawGalaxy(
  gl: WebGL2RenderingContext,
  pipe: GalaxyPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
